import DatePicker, { registerLocale } from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import ru from 'date-fns/locale/ru';

import { useMemo } from 'react';
import { useAppSelector } from 'app/store/store';
import i18n from 'i18next';
import clsx from 'clsx';
import moment from 'moment';
import { Icon } from '../Icon';

import styles from './PlaybackDatePicker.module.scss';

interface PlaybackDatePickerProps {
  setPlay: React.Dispatch<React.SetStateAction<boolean>>;
  setDate: React.Dispatch<React.SetStateAction<Date>>;
  selected: Date;
  disabled: boolean;
  repeat: boolean;
  setRepeat: React.Dispatch<React.SetStateAction<boolean>>;
  currentAnimationTime: string | null;
  progress: number;
}

export const PlaybackDatePicker = ({
  setPlay,
  setDate,
  selected,
  disabled,
  setRepeat,
  repeat,
  progress,
  currentAnimationTime,
}: PlaybackDatePickerProps) => {
  registerLocale('ru', ru);
  const { t } = useTranslation();
  const profile = useAppSelector((state) => state.userSlice.profile);
  const timeFormat: string = useMemo(() => profile?.ui?.['timeFormat'] || 'HH:mm:ss', [profile]);
  const timeFormatPicker = useMemo(() => {
    if (timeFormat.slice(0, 2) === 'hh') {
      return `${timeFormat.slice(0, 5)} aa`;
    } else {
      return `${timeFormat.slice(0, 5)}`;
    }
  }, [timeFormat]);

  const dateFormatPicker = useMemo(() => `dd.MM.yyyy, ${timeFormatPicker}`, [timeFormatPicker]);

  const maxDate = moment().utc().subtract(1, 'day').toDate();

  const valueFromConfig = useAppSelector((state) => state.userSlice.uiConfig?.playbackMaxDepth);

  const minDate = useMemo(() => {
    const depthDuration = moment.duration(valueFromConfig).asMilliseconds();
    return moment().utc().subtract(depthDuration, 'milliseconds').toDate();
  }, [valueFromConfig]);

  const passedTime = (time: Date) => {
    const currentDate = new Date();
    const selectedDate = new Date(time);

    return currentDate.getTime() > selectedDate.getTime();
  };

  return (
    <div className={styles.playback_date_picker}>
      <div className={styles.top_container}>
        <div>{t('DatePickerLabel')}</div>
        <DatePicker
          disabled={disabled}
          wrapperClassName={styles.date_picker_wrapper}
          popperClassName={styles.date_picker_popper}
          locale={i18n.language}
          showPopperArrow={false}
          selected={selected}
          onChange={(date: Date) => setDate(date)}
          showTimeSelect
          timeCaption={t('DatePickerTimeCaption')}
          monthClassName={() => styles.header}
          timeIntervals={30}
          placeholderText={t('DatePickerPlaceholderText')}
          dateFormat={dateFormatPicker}
          minDate={minDate}
          maxDate={maxDate}
          //Нельзя поменять локаль
          timeFormat={timeFormatPicker}
          filterTime={passedTime}
        />
        <div className={styles.buttons}>
          <Icon
            name="play"
            className={clsx({ [styles.buttons_active]: disabled })}
            onClick={() => {
              if (disabled) {
                setPlay(false);
                setRepeat(false);
              } else {
                setPlay(true);
              }
            }}
          />
          <Icon
            name="repeat"
            className={repeat ? styles.buttons_active : styles.repeat}
            onClick={() => {
              setRepeat(!repeat);
            }}
          />
        </div>
      </div>
      <div className={styles.progressBar}>
        <div className={styles.progressBarTrack} />
        <div className={styles.progressBall} style={{ left: `calc(${progress}% )` }} />
      </div>
      <div className={styles.dates_container}>
        <span className={styles.current_date}>{currentAnimationTime ? `${currentAnimationTime}` : <>&nbsp;</>}</span>
      </div>
    </div>
  );
};
